import { Calendar, Download, FileText, Package } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../lib/api-client";
import { showToast } from "../../lib/toast";
import {
  GoodsReceipt,
  goodsReceiptService,
  GRN_STATUS_COLORS,
  GRN_STATUS_LABELS,
} from "../../services/goods-receipt.service";

export function GoodsReceiptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isSupplierBills = location.pathname.startsWith("/supplier-bills");
  const [receipt, setReceipt] = useState<GoodsReceipt | null>(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (id) {
      loadReceipt();
    }
  }, [id]);

  const loadReceipt = async () => {
    try {
      setLoading(true);
      const data = await goodsReceiptService.getGoodsReceipt(id!);
      setReceipt(data);
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to load goods receipt",
      );
      navigate("/goods-receipts");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUnitsPerPack = (item: any) =>
    item.unitsPerPack && item.unitsPerPack > 0 ? item.unitsPerPack : 1;

  const getFreeBaseQuantity = (item: any) =>
    (item.freeQuantity || 0) * getUnitsPerPack(item);

  const getBaseQuantity = (item: any) => {
    if (typeof item.baseQuantityReceived === "number") {
      const chargeableBase =
        item.baseQuantityReceived - getFreeBaseQuantity(item);
      return Math.max(0, chargeableBase);
    }
    return item.quantity * getUnitsPerPack(item);
  };

  const getTotalBaseQuantity = (item: any) => {
    if (typeof item.baseQuantityReceived === "number") {
      return item.baseQuantityReceived;
    }
    return (item.quantity + (item.freeQuantity || 0)) * getUnitsPerPack(item);
  };

  const handleDownloadPDF = async () => {
    if (!receipt) return;

    try {
      const response = await apiClient.get(
        `/goods-receipts/${receipt.id}/pdf`,
        { responseType: "blob" },
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `GRN_${receipt.grnNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success("PDF downloaded successfully");
    } catch (err: any) {
      showToast.error(err.response?.data?.message || "Failed to download PDF");
    }
  };

  const handleComplete = async () => {
    if (!receipt) return;
    try {
      setUpdatingStatus(true);
      const updated = await goodsReceiptService.completeGoodsReceipt(
        receipt.id,
      );
      setReceipt(updated);
      showToast.success("GRN completed successfully");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to complete goods receipt",
      );
      // Refresh to sync UI with actual DB status (e.g. completed by another session)
      await loadReceipt();
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancel = async () => {
    if (!receipt) return;
    try {
      setUpdatingStatus(true);
      const updated = await goodsReceiptService.cancelGoodsReceipt(receipt.id);
      setReceipt(updated);
      showToast.success("GRN cancelled successfully");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to cancel goods receipt",
      );
      // Refresh to sync UI with actual DB status
      await loadReceipt();
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Goods receipt not found</div>
      </div>
    );
  }

  const statusColor = GRN_STATUS_COLORS[receipt.status];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() =>
            navigate(isSupplierBills ? "/supplier-bills" : "/goods-receipts")
          }
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to {isSupplierBills ? "Supplier Bills" : "Goods Receipts"}
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isSupplierBills ? "Bill" : "GRN"} #{receipt.grnNumber}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Received on {formatDate(receipt.grnDate)}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {["COMPLETED", "PARTIALLY_RETURNED"].includes(receipt.status) &&
              receipt.items.some(
                (item) => (item.returnableQuantity ?? 0) > 0,
              ) && (
                <button
                  onClick={() =>
                    navigate(`/purchase-returns/create?grnId=${receipt.id}`)
                  }
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Return
                </button>
              )}
            {receipt.purchaseInvoices?.[0] && (
              <button
                onClick={() =>
                  navigate(`/supplier-bills/${receipt.purchaseInvoices![0].id}`)
                }
                className="px-3 py-1.5 text-sm border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50"
              >
                View Supplier Bill
              </button>
            )}
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}
            >
              {GRN_STATUS_LABELS[receipt.status]}
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Receipt Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Receipt Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">GRN Date</p>
                  <p className="font-medium">{formatDate(receipt.grnDate)}</p>
                </div>
              </div>
              {receipt.supplierInvoiceNo && (
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Invoice Number</p>
                    <p className="font-medium">{receipt.supplierInvoiceNo}</p>
                  </div>
                </div>
              )}
              {receipt.supplierInvoiceDate && (
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Invoice Date</p>
                    <p className="font-medium">
                      {formatDate(receipt.supplierInvoiceDate)}
                    </p>
                  </div>
                </div>
              )}
              {receipt.purchaseOrder && (
                <div className="flex items-start">
                  <FileText className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Purchase Order</p>
                    <button
                      onClick={() =>
                        navigate(`/purchase-orders/${receipt.purchaseOrderId}`)
                      }
                      className="font-medium text-blue-600 hover:text-blue-800"
                    >
                      {receipt.purchaseOrder.poNumber}
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-start">
                <Package className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Total Items</p>
                  <p className="font-medium">{receipt.items?.length || 0}</p>
                </div>
              </div>
              <div className="flex items-start">
                <FileText className="w-5 h-5 text-gray-400 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Total Amount</p>
                  <p className="font-medium text-lg">
                    {formatCurrency(receipt.totalAmount)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {receipt.shelfLifeWarnings &&
            receipt.shelfLifeWarnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-amber-900 mb-3">
                  Short Shelf-Life Warnings
                </h2>
                <div className="space-y-2 text-sm text-amber-900">
                  {receipt.shelfLifeWarnings.map((warning) => (
                    <div key={`${warning.productId}-${warning.batchNumber}`}>
                      {warning.productName} batch {warning.batchNumber} expires
                      in {warning.remainingDays} days.
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Items */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Received Items
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Product
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Batch
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Expiry
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Qty
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Free
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Unit Price
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      MRP
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {receipt.items?.map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {item.product.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            SKU: {item.product.sku}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs font-mono">
                          {item.batchNumber}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="text-xs">
                          {formatDate(item.expiryDate)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="font-medium">
                          {getBaseQuantity(item)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">
                          {item.product.unit}
                        </span>
                        {getUnitsPerPack(item) > 1 && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            ({item.quantity} pack × {getUnitsPerPack(item)})
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span
                          className={`font-medium ${item.freeQuantity > 0 ? "text-green-600" : "text-gray-400"}`}
                        >
                          {getFreeBaseQuantity(item)}
                        </span>
                        {item.freeQuantity > 0 && getUnitsPerPack(item) > 1 && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            ({item.freeQuantity} pack × {getUnitsPerPack(item)})
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {formatCurrency(item.unitPrice)}
                      </td>
                      <td className="px-3 py-3 text-right">
                        {formatCurrency(item.mrp)}
                      </td>
                      <td className="px-3 py-3 text-right font-medium">
                        {formatCurrency(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total */}
            <div className="mt-6 border-t pt-4">
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(receipt.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Discount</span>
                    <span>{formatCurrency(receipt.discountAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>GST</span>
                    <span>{formatCurrency(receipt.taxAmount)}</span>
                  </div>
                  {(receipt.freightAmount > 0 ||
                    receipt.handlingAmount > 0 ||
                    receipt.otherCharges > 0) && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Additional Charges</span>
                      <span>
                        {formatCurrency(
                          Number(receipt.freightAmount || 0) +
                            Number(receipt.handlingAmount || 0) +
                            Number(receipt.otherCharges || 0),
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Amount</span>
                    <span>{formatCurrency(receipt.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {receipt.notes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Notes
              </h2>
              <p className="text-sm text-gray-600">{receipt.notes}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Supplier Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Supplier Details
            </h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">
                  {typeof receipt.supplier === "object"
                    ? receipt.supplier.name
                    : "N/A"}
                </p>
              </div>
              {typeof receipt.supplier === "object" &&
                "phone" in receipt.supplier &&
                receipt.supplier.phone && (
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{receipt.supplier.phone}</p>
                  </div>
                )}
              {typeof receipt.supplier === "object" &&
                "email" in receipt.supplier &&
                receipt.supplier.email && (
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-sm">
                      {receipt.supplier.email}
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Actions
            </h2>
            <div className="space-y-3">
              {receipt.status === "DRAFT" && (
                <div className="space-y-2">
                  <button
                    onClick={handleComplete}
                    disabled={updatingStatus}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {updatingStatus ? "Updating..." : "Complete GRN"}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={updatingStatus}
                    className="w-full px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                  >
                    Cancel GRN
                  </button>
                </div>
              )}
              <button
                onClick={handleDownloadPDF}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </button>

              {receipt.purchaseInvoices?.[0] && (
                <button
                  onClick={() =>
                    navigate(
                      `/supplier-bills/${receipt.purchaseInvoices![0].id}`,
                    )
                  }
                  className="w-full px-4 py-2 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50"
                >
                  View Supplier Bill
                </button>
              )}

              <button
                onClick={() => navigate(`/inventory`)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                View Stock
              </button>
            </div>
          </div>

          {/* Batch Summary */}
          {receipt.items && receipt.items.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Batch Summary
              </h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Batches Created:</span>
                  <span className="font-medium">{receipt.items.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Quantity:</span>
                  <span className="font-medium">
                    {receipt.items.reduce(
                      (sum, item) => sum + getTotalBaseQuantity(item),
                      0,
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Free Quantity:</span>
                  <span className="font-medium text-green-600">
                    {receipt.items.reduce(
                      (sum, item) => sum + getFreeBaseQuantity(item),
                      0,
                    )}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
